import { COL } from "../api/sheets";
import { Row } from "../types/sheets";

function escapeXml(s: unknown): string {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

export function driveImageUrl(fileId: string): string {
  return `https://drive.google.com/uc?export=view&id=${fileId}`;
}

const STYLE_BLOCK = `    <Style id="icon-ci-1-normal">
      <IconStyle>
        <scale>1.1</scale>
        <Icon><href>images/icon-1.png</href></Icon>
        <hotSpot x="15" xunits="pixels" y="15" yunits="insetPixels"/>
      </IconStyle>
      <LabelStyle><scale>0</scale></LabelStyle>
    </Style>
    <Style id="icon-ci-1-highlight">
      <IconStyle>
        <scale>1.1</scale>
        <Icon><href>images/icon-1.png</href></Icon>
        <hotSpot x="15" xunits="pixels" y="15" yunits="insetPixels"/>
      </IconStyle>
      <LabelStyle><scale>1.1</scale></LabelStyle>
    </Style>
    <StyleMap id="icon-ci-1">
      <Pair><key>normal</key><styleUrl>#icon-ci-1-normal</styleUrl></Pair>
      <Pair><key>highlight</key><styleUrl>#icon-ci-1-highlight</styleUrl></Pair>
    </StyleMap>`;

function imgTag(url: string): string {
  return `<img src="${escapeXml(url)}" height="200" width="auto" />`;
}

function normalizeCoord(v: unknown): string {
  return String(v == null ? "" : v).replace(/,/g, ".");
}

interface PlacemarkOptions {
  imagesByRowId: Map<string, string[]>;
  commonPictureUrl: string;
}

function placemark(r: Row, { imagesByRowId, commonPictureUrl }: PlacemarkOptions): string {
  const id = (String(r[COL["ID"]] ?? "")).trim();
  const dept = r[COL["部局、施設名"]] ?? "";
  const bldg = r[COL["建物"]] ?? "";
  const floorRaw = String(r[COL["階"]] ?? "").trim();
  const floor = floorRaw && !/階$/.test(floorRaw) ? `${floorRaw}階` : floorRaw;
  const installLocation = r[COL["設置場所"]] ?? "";
  const lat = normalizeCoord(r[COL["緯度"]]);
  const lng = normalizeCoord(r[COL["経度"]]);

  const name = [dept, bldg, floor].filter(Boolean).join("　");

  const dispenserPics = (id && imagesByRowId.get(id)) || [];
  const image01 = dispenserPics[0] || "";
  const extraDispenser = dispenserPics.slice(1);
  const image02 = commonPictureUrl || "";

  const descParts: string[] = [];
  if (image01) descParts.push(imgTag(image01));
  descParts.push(
    `生理用品無償提供用のMeWディスペンサーが設置してあります。<br/>設置場所：${escapeXml(installLocation)}`
  );
  for (const u of extraDispenser) descParts.push(imgTag(u));
  if (image02) descParts.push(imgTag(image02));
  const desc = descParts.join("<br><br>");

  const allMedia = [...dispenserPics];
  if (image02) allMedia.push(image02);
  const mediaBlock =
    allMedia.length > 0
      ? `
      <ExtendedData>
        <Data name="gx_media_links">
          <value><![CDATA[${allMedia.join(" ")}]]></value>
        </Data>
      </ExtendedData>`
      : "";

  return `    <Placemark>
      <name>${escapeXml(name)}</name>
      <description><![CDATA[${desc}]]></description>
      <styleUrl>#icon-ci-1</styleUrl>${mediaBlock}
      <Point>
        <coordinates>${escapeXml(lng)},${escapeXml(lat)},0</coordinates>
      </Point>
    </Placemark>`;
}

export function buildKml(
  rows: Row[],
  { mapName = "MeW Dispensers", imagesByRowId = new Map<string, string[]>(), commonPictureUrl = "" } = {}
): string {
  const placemarks = rows
    .filter((r) => r[COL["緯度"]] && r[COL["経度"]])
    .map((r) => placemark(r, { imagesByRowId, commonPictureUrl }))
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(mapName)}</name>
${STYLE_BLOCK}
${placemarks}
  </Document>
</kml>`;
}

export function todayStamp(): string {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
}

export function timestamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}
